import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
type Category = {
  id: string;
  name: string;
  image: string;
  status: string;
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Category>();

const RealtorCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const token = getAuthCookie("sessionToken");
  const location = useLocation();
  const navigate = useNavigate();

  // Determine the base route based on current location
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.categories
  );

  const [rowCount, setRowCount] = useState(0);
  const itemsPerPage = 10;
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const topRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              fields: ["name", "image", "status"]
            })
          }
        );

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }

        setCategories(result?.data ?? []);
        setRowCount(result?.total ?? 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token, globalFilter, pagination]);

  useEffect(() => {
    const filtered = categories?.filter((cat) =>
      cat.name.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredCategories(filtered);
  }, [filterText, categories]);

  const handleDelete = async (catId: string) => {
    setDeleting(catId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/categories/${catId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete the category.");

      showSuccess("Category deleted successfully!");
      setCategories((prev) => prev?.filter((cat) => cat.id !== catId));
      setDeleting(null);
      // navigate(0);
    } catch (err) {
      showError("Failed to delete the category.");
      console.error(err);
      setDeleting(null);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setDeleteTarget({
      id: category.id,
      name: category.name || "Category",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  //   if (loading) {
  //     return <div>Loading...</div>;
  //   }
  const imageUrlMap = usePresignedMap(categories, "image", token);

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => (
          <div className="th-text text-center  S-No">S.No.</div>
        ),
        cell: (info) => (
          <div className="text-sm text-center S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text text-center  S-No-2">Image</div>
        ),
        cell: (info) => {
          const raw = info.row.original?.image as string | undefined;
          const resolved = getImageSrc(raw, imageUrlMap);
          const src = resolved || imagecustomRounded;
          return (
            <div className="text-sm text-center S-No-2">
              <img
                src={src}
                alt="Category"
                style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                }}
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor(
        (row: Category) => `${row?.name}`,
        {
          id: "name",
          header: () => (
            <div className="th-text text-center ">Name</div>
          ),
          cell: (info) => (
            <div className="text-sm text-center">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor("status", {
        header: () => (
          <div className="th-text text-center ">Status</div>
        ),
        cell: (info) => {
          const value = info.getValue() as string | boolean;
          const isActive = value === true || value === "true";

          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm text-center">
              <input
                type="radio"
                className={className}
                checked={isActive}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: false,

        // cell: (info) => (
        //   <div className="text-sm text-center">
        //     {info.getValue() ? (
        //       <span className="status-badge status-accepted-btn">Active</span>
        //     ) : (
        //       <span className="status-badge status-draft-btn">Inactive</span>
        //     )}
        //   </div>
        // ),
        // enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text text-center ">Actions</div>
        ),
        cell: (info) => {
          const categoryInfo = info.row.original;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              {canEdit && (
                <EditIconButton
                  to={`${baseRoute}/editCategorie/${categoryInfo.id}`}
                  className="icon-square"
                  title="Edit"
                />
              )}
              {canDelete && (
                <DeleteIconButton
                  onClick={() => handleDeleteClick(categoryInfo)}
                  className="icon-square danger"
                  title="Delete"
                  disabled={deleting === categoryInfo.id}
                />
              )}

            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, imageUrlMap, canView, canEdit, canDelete]);

  // const columns = useMemo(() => {
  //   return [
  //     columnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
  //     }),
  //     columnHelper.display({
  //       id: "image",
  //       header: "Image",
  //       cell: (info) => {
  //         const imageUrl = info.row.original.image;
  //         return imageUrl ? (
  //           <img
  //             src={`${process.env.REACT_APP_BASE_URL}${imageUrl}`}
  //             height={50}
  //             alt="img"
  //             // onError={(e) => {
  //             //   (e.target as HTMLImageElement).src = "/fallback.jpg"; // Optional fallback
  //             // }}
  //           />
  //         ) : (
  //           <span>No Image</span>
  //         );
  //       },
  //     }),
  //     columnHelper.accessor("name", {
  //       header: "Name",
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.accessor("status", {
  //       header: "Status",
  //       cell: (info) => (info.getValue() ? <span className="status-badge status-accepted-btn">Active</span> : <span className="status-badge status-draft-btn">Inactive</span>),
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const category = info.row.original;
  //         return (
  //           <div className="d-flex gap-2">
  //             <Link
  //               to={`${baseRoute}/editCategorie/${category.id}`}
  //               className="edit me-3"
  //             >
  //               <i className="ri-pencil-fill align-bottom fs-20"></i>{" "}
  //             </Link>

  //             <button
  //               onClick={() =>
  //                 window.confirm(
  //                   "Are you sure you want to delete this category?"
  //                 ) && handleDelete(category.id)
  //               }
  //               className="del border-0 p-0"
  //             >
  //               <i className="ri-delete-bin-fill align-bottom fs-18"></i>
  //             </button>
  //           </div>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting]);


  const table = useReactTable({
    data: categories || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  return (
    <div className="content container" ref={topRef}>
      {/* <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          <Link to="/realtor/addCategory" className="btn btn-primary  table-addbutton">
            + Add Categories
          </Link>
        </div>
      </div> */}
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Categories</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Name"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // :white_check_mark: Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
            {canCreate && (
              <Link to={`${baseRoute}/addCategorie`} className="btn btn-primary  table-addbutton">
                + Add Categories
              </Link>
            )}
            {/* <ExportAsExcel
                                  data={exportRows}
                                  headers={[
                                    "Company Name",
                                    "First Name",
                                    "Last Name",
                                    "Phone",
                                    "Email",
                                    "License Status",
                                    "Status",
                                  ]}
                                  fileName="BrokerageFirms"
                                >
                                  {(props) => (
                                    <button {...props} className="export-button">
                                      <i className="ri-file-excel-2-line me-2" />
                                      Export to Excel
                                    </button>
                                  )}
                                </ExportAsExcel> */}
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                          orders
                        </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      )}
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Category"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default RealtorCategories;
